/**
 * Screen 4 — accept the terms and pay the down payment.
 *
 * Reached from the trial, or directly from screen two when the prospect
 * chooses to skip the trial. This is the only screen in the flow that calls
 * the backend: POST /api/self-serve/signup records the submission and opens a
 * real Stripe Checkout Session in the same call, handing back its URL, and
 * this page sends the whole browser there (off-domain, so a router navigate()
 * cannot do it). The server computes the amount from roleBand + hires
 * (avrio-backend#167); the figures shown here come from the same rate card,
 * kept in sync by hand. Provisioning waits on Stripe's webhook confirming the
 * payment; see pages/payment-complete for where the browser lands after that.
 */

import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMachine } from '@xstate/react';
import { Button, Checkbox, Form, message } from 'antd';
import { AxiosError } from 'axios';
import FlowShell from '../../components/flow-shell';
import { useSelfServe } from '../../context';
import { formatUsd, RoleType } from '../../utils/quote';
import { SALARY_BAND_LABEL } from '../../utils/flex';
import {
  FLEX_COPY,
  FLEX_PRIVACY_URL,
  FLEX_TERMS_URL,
} from '../../utils/flexCopy';
import {
  selfServeSignupFSM,
  EventType,
  State,
  RoleBand,
} from '../../../../fsm/selfServe';
import styles from './index.module.css';

interface ErrorBody {
  message?: string;
}

/**
 * The company's real, live legal pages -- not a dead `#` placeholder. Reused
 * from the same convention the main app's footer already links (see
 * components/layout), rather than invented fresh for this flow.
 *
 * Worth a second look before launch, not necessarily a swap: this page ends
 * in a real paid contract (a down payment + contract value), which is a
 * different legal surface than general website usage. If the self-serve
 * contract needs its own terms distinct from these general pages, only these
 * two constants change -- nothing else on this page depends on where they
 * point.
 */
const TERMS_OF_SERVICE_URL = 'https://www.avrioai.com/terms-of-use';
const PRIVACY_POLICY_URL = 'https://www.avrioai.com/privacy-policy';

const ROLE_BAND: Record<RoleType, RoleBand> = {
  hourly: 'HOURLY',
  trades: 'TRADES',
  office: 'OFFICE',
  leadership: 'LEADERSHIP',
};

const errorMessage = (error?: AxiosError): string =>
  (error?.response?.data as ErrorBody | undefined)?.message ??
  error?.message ??
  'Something went wrong. Please try again.';

const Payment = () => {
  const navigate = useNavigate();
  const { cardOnFile, answers, quote, flexQuote, account } = useSelfServe();
  const [state, send] = useMachine(selfServeSignupFSM);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (state.matches(State.SUBMITTED) && state.context.checkoutUrl) {
      // A full navigation, not react-router: Stripe Checkout is off-domain,
      // so there is nowhere in this app's route tree to send() to instead.
      window.location.href = state.context.checkoutUrl;
    }
    if (state.matches(State.ERROR)) {
      messageApi.open({
        type: 'error',
        content: errorMessage(state.context.error),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value]);

  const onFinish = useCallback(
    (values: { acceptedTerms: boolean }) => {
      // The router guarantees all three before this route renders; guarding
      // here (rather than an early return above) keeps every hook call
      // unconditional, which is what react-hooks/rules-of-hooks requires.
      if (!account) return;
      if (cardOnFile) {
        if (!flexQuote) return;
        // Card on file: no price input at all. The server opens a card-saving Checkout and each hire is charged at
        // offer from the hire's own salary band, so the band chosen here is never sent as something to charge.
        send({
          type: EventType.SUBMIT,
          payload: {
            firstName: account.firstName,
            lastName: account.lastName,
            workEmail: account.email,
            companyName: account.company,
            currency: 'USD',
            termsAccepted: values.acceptedTerms,
          },
        });
        return;
      }
      if (!quote || !answers) return;
      send({
        type: EventType.SUBMIT,
        payload: {
          firstName: account.firstName,
          lastName: account.lastName,
          workEmail: account.email,
          companyName: account.company,
          // The inputs to the price, not the price: the server computes the
          // amount it charges from these.
          roleBand: ROLE_BAND[answers.roleType],
          hires: quote.hires,
          currency: 'USD',
          // The form's own validator already refuses to call onFinish unless
          // this is checked, so it is always true here - sent anyway rather
          // than hardcoded, so the backend's check is against what the
          // caller actually submitted, not an assumption this page makes on
          // its behalf.
          termsAccepted: values.acceptedTerms,
        },
      });
    },
    [send, cardOnFile, flexQuote, quote, answers, account],
  );

  if (!account) return null;

  if (cardOnFile) {
    if (!flexQuote) return null;
    return (
      <FlowShell
        step={4}
        title={FLEX_COPY.addCardTitle}
        subtitle={FLEX_COPY.addCardSubtitle}
      >
        {contextHolder}
        <div className={styles.recap}>
          <h2 className={styles.recapTitle}>Your plan</h2>
          <dl className={styles.recapFigures}>
            <div>
              <dt className={styles.recapLabel}>Salary band</dt>
              <dd>{SALARY_BAND_LABEL[flexQuote.band]}</dd>
            </div>
            <div>
              <dt className={styles.recapLabel}>Per hire, charged at offer</dt>
              <dd>{formatUsd(flexQuote.pricePerHire)}</dd>
            </div>
            <div>
              <dt className={styles.recapLabel}>Due today</dt>
              <dd>{formatUsd(0)}</dd>
            </div>
          </dl>
        </div>

        <Form
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          className={styles.form}
        >
          <p className={styles.whatHappens}>
            For {account.company}, account owner {account.firstName}{' '}
            {account.lastName} ({account.email}). {FLEX_COPY.otherBandsNote}{' '}
            {FLEX_COPY.bankConfirmNote}
          </p>

          {/* S5: LEGAL, BLOCKING FOR LAUNCH. Draft authorization pending Eula / counsel; see utils/flexCopy.ts. */}
          <p className={styles.whatHappens} data-testid="card-authorization">
            {FLEX_COPY.authorization}
          </p>

          <Form.Item
            name="acceptedTerms"
            valuePropName="checked"
            className={styles.termsItem}
            validateTrigger="onChange"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          'Please accept the Terms of Service and Privacy Policy to continue.',
                        ),
                      ),
              },
            ]}
          >
            {/* S6: LEGAL, BLOCKING FOR LAUNCH. Which terms a paid Flex plan accepts; general pages until decided. */}
            <Checkbox>
              I agree to the{' '}
              <a href={FLEX_TERMS_URL} target="_blank" rel="noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href={FLEX_PRIVACY_URL} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              , and to the authorization above.
            </Checkbox>
          </Form.Item>

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className={styles.cta}
            loading={state.context.isLoading}
          >
            {FLEX_COPY.addCardCta}
          </Button>
          <Button
            type="link"
            className={styles.back}
            onClick={() => navigate('/selfserve/trial')}
          >
            Try it on a job description first
          </Button>
        </Form>
      </FlowShell>
    );
  }

  if (!quote) return null;

  return (
    <FlowShell
      step={4}
      title="Activate your plan"
      subtitle="Pay the amount due up front by card to activate your account."
    >
      {contextHolder}
      <div className={styles.recap}>
        <h2 className={styles.recapTitle}>Your plan</h2>
        <dl className={styles.recapFigures}>
          <div>
            <dt className={styles.recapLabel}>Hires a year</dt>
            <dd>{quote.hires.toLocaleString('en-US')}</dd>
          </div>
          <div>
            <dt className={styles.recapLabel}>Rate per hire</dt>
            <dd>{formatUsd(quote.ratePerHire)}</dd>
          </div>
          <div>
            <dt className={styles.recapLabel}>Contract value</dt>
            <dd>{formatUsd(quote.contractValue)}</dd>
          </div>
          <div>
            <dt className={styles.recapLabel}>Due now</dt>
            <dd>{formatUsd(quote.downPayment)}</dd>
          </div>
        </dl>
      </div>

      <Form
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        className={styles.form}
      >
        <p className={styles.whatHappens}>
          For {account.company}, account owner {account.firstName}{' '}
          {account.lastName} ({account.email}). You&apos;ll pay{' '}
          {formatUsd(quote.downPayment)} on Stripe&apos;s secure checkout; the
          remaining {formatUsd(quote.drawDown)} is drawn down as you hire.
        </p>

        <Form.Item
          name="acceptedTerms"
          valuePropName="checked"
          className={styles.termsItem}
          validateTrigger="onChange"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        'Please accept the Terms of Service and Privacy Policy to continue.',
                      ),
                    ),
            },
          ]}
        >
          <Checkbox>
            I agree to the{' '}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </Checkbox>
        </Form.Item>

        <Button
          type="primary"
          size="large"
          htmlType="submit"
          className={styles.cta}
          loading={state.context.isLoading}
        >
          Continue to payment
        </Button>
        <Button
          type="link"
          className={styles.back}
          onClick={() => navigate('/selfserve/trial')}
        >
          Try it on a job description first
        </Button>
      </Form>
    </FlowShell>
  );
};

export default Payment;
